import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useThemePalette } from "common/hooks/theme_palette";
import PackageItemsPanel from "./items";

const MetaItem = ({ label, value }) => (
  <Box>
    <Typography fontSize="12px" color="#8A8A8A" mb={0.5}>
      {label}
    </Typography>
    <Typography fontSize="15px" fontWeight="500" color="#2D2D2D">
      {value ?? "-"}
    </Typography>
  </Box>
);

const PackageDetails = ({ details, canUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oliveGreen } = useThemePalette();

  const nameEn =
    details?.localized_data?.name_en ||
    details?.localized_data?.en?.name ||
    details?.name_en ||
    details?.name ||
    "";
  const nameAr =
    details?.localized_data?.name_ar ||
    details?.localized_data?.ar?.name ||
    details?.name_ar ||
    "";
  const descriptionEn =
    details?.localized_data?.description_en ||
    details?.description_en ||
    details?.description ||
    "";
  const descriptionAr =
    details?.localized_data?.description_ar || details?.description_ar || "";
  const gallery = details?.images || [];
  const itemsCount = details?.items_count || {};
  const totalItems =
    (itemsCount.mandatory || 0) +
    (itemsCount.included || 0) +
    (itemsCount.additional || 0);

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="22px" fontWeight="600" mb={0.5}>
            {t("events_packages.details")}
          </Typography>
          <Typography fontSize="14px" color="#797979">
            #{details?.id} · {totalItems} {t("events_packages.items_count_label")}
          </Typography>
        </Box>
        {canUpdate && (
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => navigate(`?type=form&id=${details?.id}`)}
            sx={{
              borderColor: oliveGreen,
              color: oliveGreen,
              textTransform: "none",
              "&:hover": {
                borderColor: oliveGreen,
                backgroundColor: "rgba(0,0,0,0.02)",
              },
            }}
          >
            {t("common.edit")}
          </Button>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: "1px solid #E8E8E8",
          borderRadius: "10px",
          mb: 2.5,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={2.5}
          alignItems={{ xs: "flex-start", md: "flex-start" }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: 220 },
              height: 140,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#F5F5F5",
              border: "1px solid #EEEEEE",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {details?.cover_image ? (
              <img
                src={details.cover_image}
                alt={nameEn}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography fontSize="12px" color="#A0A0A0">
                {t("chat.no_image")}
              </Typography>
            )}
          </Box>

          <Stack gap={2} flex={1} width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
              flexWrap="wrap"
            >
              <Box>
                <Typography fontSize="18px" fontWeight="600">
                  {nameEn}
                </Typography>
                {nameAr && (
                  <Typography fontSize="15px" color="#5A5A5A" dir="rtl">
                    {nameAr}
                  </Typography>
                )}
              </Box>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={t(`events_packages.levels.${details?.level}`, {
                    defaultValue: details?.level,
                  })}
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "rgba(156, 39, 176, 0.1)",
                    color: "#7B1FA2",
                  }}
                />
                <Chip
                  size="small"
                  label={
                    details?.is_active
                      ? t("events_packages.is_active")
                      : t("common.no")
                  }
                  sx={{
                    fontWeight: 600,
                    backgroundColor: details?.is_active
                      ? "rgba(76, 175, 80, 0.12)"
                      : "rgba(158, 158, 158, 0.16)",
                    color: details?.is_active ? "#2E7D32" : "#616161",
                  }}
                />
              </Stack>
            </Stack>

            {(descriptionEn || descriptionAr) && (
              <Box>
                {descriptionEn && (
                  <Typography fontSize="14px" color="#5A5A5A">
                    {descriptionEn}
                  </Typography>
                )}
                {descriptionAr && (
                  <Typography fontSize="14px" color="#5A5A5A" dir="rtl">
                    {descriptionAr}
                  </Typography>
                )}
              </Box>
            )}

            <Divider />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={{ xs: 1.5, sm: 3 }}
              flexWrap="wrap"
            >
              <MetaItem
                label={t("events_packages.event_type")}
                value={details?.event_type?.name}
              />
              <MetaItem
                label={t("events_packages.base_price")}
                value={`${details?.base_price ?? "-"} ${details?.currency || "SAR"}`}
              />
              <MetaItem
                label={t("events_packages.max_guests")}
                value={details?.max_guests}
              />
              <MetaItem
                label={t("events_packages.display_priority")}
                value={details?.display_priority}
              />
            </Stack>

            <Stack direction="row" gap={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`${t("events_packages.kinds.mandatory")}: ${itemsCount.mandatory || 0}`}
              />
              <Chip
                size="small"
                label={`${t("events_packages.kinds.included")}: ${itemsCount.included || 0}`}
              />
              <Chip
                size="small"
                label={`${t("events_packages.kinds.additional")}: ${itemsCount.additional || 0}`}
              />
            </Stack>
          </Stack>
        </Stack>

        {gallery.length > 0 && (
          <Box mt={2.5}>
            <Typography fontSize="12px" color="#8A8A8A" mb={1}>
              {t("events_packages.images")}
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {gallery.map((url) => (
                <Box
                  key={url}
                  sx={{
                    width: 96,
                    height: 72,
                    borderRadius: "8px",
                    overflow: "hidden",
                    border: "1px solid #EEEEEE",
                  }}
                >
                  <img
                    src={url}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Paper>

      {details?.id && (
        <PackageItemsPanel
          packageId={details.id}
          eventTypeId={details?.event_type?.id}
          canUpdate={canUpdate}
          items={details?.items}
          itemsCount={details?.items_count}
        />
      )}
    </Box>
  );
};

export default PackageDetails;
